import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Target, Plus, Trophy, Flame, Star, Gift, 
  Plane, Home, Car, GraduationCap, Heart, Sparkles,
  TrendingUp, Calendar, PiggyBank, Edit, Trash2,
  Award, Zap, Crown, Medal
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';

const GOAL_ICONS = {
  vacation: { icon: Plane, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  home: { icon: Home, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  car: { icon: Car, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' },
  education: { icon: GraduationCap, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  emergency: { icon: Heart, color: 'text-pink-500', bg: 'bg-pink-100 dark:bg-pink-900/30' },
  other: { icon: Target, color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
};

const BADGES = [
  { id: 'first_goal', name: 'Primera Meta', icon: Target, description: 'Crear tu primera meta de ahorro', xp: 50 },
  { id: 'saver_10', name: 'Ahorrador Novato', icon: PiggyBank, description: 'Ahorrar €100', xp: 100 },
  { id: 'saver_100', name: 'Ahorrador Experto', icon: Trophy, description: 'Ahorrar €1,000', xp: 250 },
  { id: 'saver_1000', name: 'Ahorrador Maestro', icon: Crown, description: 'Ahorrar €10,000', xp: 500 },
  { id: 'streak_7', name: 'Racha Semanal', icon: Flame, description: '7 días consecutivos ahorrando', xp: 75 },
  { id: 'streak_30', name: 'Racha Mensual', icon: Zap, description: '30 días consecutivos ahorrando', xp: 200 },
  { id: 'goal_complete', name: 'Meta Cumplida', icon: Star, description: 'Completar una meta de ahorro', xp: 150 },
  { id: 'goal_5', name: 'Conquistador', icon: Medal, description: 'Completar 5 metas', xp: 400 },
];

const SavingsGoals = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);
  
  const [goals, setGoals] = useState([]);
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({ totalSaved: 0, streak: 0, level: 1, xp: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [addMoneyDialogOpen, setAddMoneyDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    icon: 'other',
    deadline: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/savings-goals');
      setGoals(response.data.goals || []);
      setBadges(response.data.badges || []);
      setStats(response.data.stats || { totalSaved: 0, streak: 0, level: 1, xp: 0 });
    } catch (error) {
      toast.error('Error al cargar las metas de ahorro');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGoal) {
        await api.put(`/savings-goals/${editingGoal.id}`, formData);
        toast.success('Meta actualizada');
      } else {
        await api.post('/savings-goals', formData);
        toast.success('¡Nueva meta creada! +50 XP');
      }
      setDialogOpen(false);
      setEditingGoal(null);
      setFormData({ name: '', target_amount: '', icon: 'other', deadline: '' });
      loadData();
    } catch (error) {
      toast.error('Error al guardar la meta');
    }
  };

  const handleAddMoney = async (amount) => {
    if (!selectedGoal || !amount) return;
    try {
      await api.post(`/savings-goals/${selectedGoal.id}/add-funds`, null, { params: { amount: parseFloat(amount) } });
      toast.success(`¡+€${amount} añadidos! +${Math.floor(amount / 10)} XP`);
      setAddMoneyDialogOpen(false);
      setSelectedGoal(null);
      loadData();
    } catch (error) {
      toast.error('Error al añadir dinero');
    }
  };

  const handleDelete = async (goalId) => {
    if (!confirm('¿Eliminar esta meta de ahorro?')) return;
    try {
      await api.delete(`/savings-goals/${goalId}`);
      toast.success('Meta eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: user?.currency || 'EUR'
    }).format(amount);
  };

  const getLevel = (xp) => Math.floor(xp / 500) + 1;
  const getXpProgress = (xp) => (xp % 500) / 5;

  return (
    <div className="space-y-6 pb-8" data-testid="savings-goals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Target className="text-primary" />
            Metas de Ahorro
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gamifica tu ahorro y alcanza tus objetivos
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-goal-btn">
              <Plus size={16} className="mr-2" />
              Nueva Meta
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingGoal ? 'Editar Meta' : 'Nueva Meta de Ahorro'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre de la meta</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Vacaciones en Japón"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Cantidad objetivo (€)</Label>
                <Input
                  type="number"
                  value={formData.target_amount}
                  onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                  placeholder="5000"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Icono</Label>
                <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vacation">✈️ Vacaciones</SelectItem>
                    <SelectItem value="home">🏠 Casa</SelectItem>
                    <SelectItem value="car">🚗 Coche</SelectItem>
                    <SelectItem value="education">🎓 Educación</SelectItem>
                    <SelectItem value="emergency">❤️ Emergencia</SelectItem>
                    <SelectItem value="other">🎯 Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha objetivo (opcional)</Label>
                <Input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="submit">{editingGoal ? 'Guardar' : 'Crear Meta'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Bar */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Crown className="w-5 h-5 text-amber-500" />
                <span className="text-2xl font-bold text-foreground">Nivel {stats.level}</span>
              </div>
              <Progress value={getXpProgress(stats.xp)} className="h-2 mt-2" />
              <p className="text-xs text-muted-foreground mt-1">{stats.xp % 500}/500 XP</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <PiggyBank className="w-5 h-5 text-emerald-500" />
                <span className="text-2xl font-bold text-foreground">{formatCurrency(stats.totalSaved)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Total ahorrado</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="text-2xl font-bold text-foreground">{stats.streak} días</span>
              </div>
              <p className="text-xs text-muted-foreground">Racha actual</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Trophy className="w-5 h-5 text-amber-500" />
                <span className="text-2xl font-bold text-foreground">{badges.length}</span>
              </div>
              <p className="text-xs text-muted-foreground">Logros</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Goals Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {goals.map((goal) => {
          const iconConfig = GOAL_ICONS[goal.icon] || GOAL_ICONS.other;
          const Icon = iconConfig.icon;
          const progress = Math.min((goal.current_amount / goal.target_amount) * 100, 100);
          const isComplete = progress >= 100;

          return (
            <Card 
              key={goal.id} 
              className={cn(
                "relative overflow-hidden transition-all",
                isComplete && "ring-2 ring-emerald-500 bg-emerald-50 dark:bg-emerald-950/20"
              )}
              data-testid={`goal-card-${goal.id}`}
            >
              {isComplete && (
                <div className="absolute top-2 right-2">
                  <Badge className="bg-emerald-500 text-white">
                    <Star size={12} className="mr-1" fill="currentColor" />
                    ¡Completada!
                  </Badge>
                </div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                  <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", iconConfig.bg)}>
                    <Icon className={cn("w-6 h-6", iconConfig.color)} />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{goal.name}</CardTitle>
                    {goal.deadline && (
                      <CardDescription className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(goal.deadline).toLocaleDateString('es-ES')}
                      </CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progreso</span>
                    <span className="font-medium">{progress.toFixed(0)}%</span>
                  </div>
                  <Progress value={progress} className="h-3" />
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold text-primary">{formatCurrency(goal.current_amount)}</span>
                    <span className="text-muted-foreground">de {formatCurrency(goal.target_amount)}</span>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button 
                      size="sm" 
                      className="flex-1"
                      onClick={() => {
                        setSelectedGoal(goal);
                        setAddMoneyDialogOpen(true);
                      }}
                      disabled={isComplete}
                    >
                      <Plus size={14} className="mr-1" />
                      Añadir
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setEditingGoal(goal);
                        setFormData({
                          name: goal.name,
                          target_amount: goal.target_amount,
                          icon: goal.icon,
                          deadline: goal.deadline || '',
                        });
                        setDialogOpen(true);
                      }}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDelete(goal.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {goals.length === 0 && !loading && (
          <Card className="col-span-full">
            <CardContent className="pt-12 pb-12 text-center">
              <Target className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No tienes metas de ahorro</h3>
              <p className="text-muted-foreground mb-4">
                Crea tu primera meta y empieza a ganar XP mientras ahorras
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus size={16} className="mr-2" />
                Crear primera meta
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Badges Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="text-amber-500" />
            Logros y Badges
          </CardTitle>
          <CardDescription>
            Desbloquea logros mientras ahorras
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {BADGES.map((badge) => {
              const Icon = badge.icon;
              const unlocked = badges.includes(badge.id);
              
              return (
                <div 
                  key={badge.id}
                  className={cn(
                    "text-center p-4 rounded-lg border transition-all",
                    unlocked 
                      ? "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800" 
                      : "bg-muted/30 border-border opacity-50"
                  )}
                >
                  <div className={cn(
                    "w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center",
                    unlocked ? "bg-amber-100 dark:bg-amber-900/50" : "bg-muted"
                  )}>
                    <Icon className={cn(
                      "w-6 h-6",
                      unlocked ? "text-amber-500" : "text-muted-foreground"
                    )} />
                  </div>
                  <p className="font-medium text-sm">{badge.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                  <Badge variant={unlocked ? "default" : "outline"} className="mt-2 text-xs">
                    +{badge.xp} XP
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add Money Dialog */}
      <Dialog open={addMoneyDialogOpen} onOpenChange={setAddMoneyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir a "{selectedGoal?.name}"</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const amount = e.target.amount.value;
            handleAddMoney(amount);
          }} className="space-y-4">
            <div className="space-y-2">
              <Label>Cantidad (€)</Label>
              <Input name="amount" type="number" step="0.01" placeholder="100" required />
            </div>
            <div className="flex gap-2">
              {[10, 50, 100, 200].map((amount) => (
                <Button 
                  key={amount} 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleAddMoney(amount)}
                >
                  €{amount}
                </Button>
              ))}
            </div>
            <DialogFooter>
              <Button type="submit">Añadir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavingsGoals;
